import { useState } from 'react'

import { Box, Text } from '@factorialco/deprecated-design-system'
import { FinanceInvoiceResourceInput, InvoiceFragment } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useEffect, useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import {
  FileWithStatus,
  FileWithStatusDropzone,
} from 'modules/finance/components/FileWithStatusDropzone'
import { useDelete } from 'modules/finance/hooks/useInvoices'
import { useUploadInvoice } from 'modules/finance/hooks/useUploadInvoice'
import { Option } from 'modules/layout/components/ResponsivePage/_PageHeader/_Actions'

export type InvoiceOption = Option & {
  onClick?: (fileWithStatus: FileWithStatus<InvoiceFragment>) => void
}

export interface InvoicesDropzoneProps {
  values?: FileWithStatus<InvoiceFragment>[]
  onUploadFinish: (filesStatus: FileWithStatus<InvoiceFragment>[]) => void
  onFileRemove?: (filwWithStatus: FileWithStatus<InvoiceFragment>) => void
  options?: InvoiceOption[]
  resources?: FinanceInvoiceResourceInput[]
  vendorId?: number
  multiple?: boolean
}

export const InvoicesDropzone = ({
  values,
  onUploadFinish,
  onFileRemove,
  options = [],
  resources,
  vendorId,
  multiple = true,
}: InvoicesDropzoneProps) => {
  const [filesStatus, setFilesStatus] = useState<FileWithStatus<InvoiceFragment>[]>([])
  const [position, setPosition] = useState<number | undefined>()
  const { uploadInvoice, getInvoice, invoice } = useUploadInvoice()
  const { delete: deleteInvoice } = useDelete()

  const { company } = useCurrentSession()

  useUnsafeEffect(() => {
    if (values) {
      setFilesStatus(values)
    }
  }, [values])

  const onDropFile = (files: File[]) => {
    const oldFileStatus = multiple ? filesStatus : []
    const newFileStatus = oldFileStatus.concat(
      files.map((el) => {
        const fileStatus: FileWithStatus<InvoiceFragment> = {
          file: el,
          status: 'waiting',
          options,
        }
        fileStatus.options = options.map((option) => ({
          ...option,
          onClick: () => option.onClick?.(fileStatus),
        }))
        return fileStatus
      })
    )
    setFilesStatus(newFileStatus)
    if (files.length > 0 && (!position || filesStatus.length === position)) {
      processFiles(company.id, newFileStatus, position ?? 0)
      track(trackingEvents.accounting.invoices.uploadInvoice)
    }
  }

  const onRemoveFile = async (fileToRemove: FileWithStatus<InvoiceFragment>) => {
    const invoiceIdToDelete = fileToRemove.result?.id
    if (invoiceIdToDelete) {
      fileToRemove.status = 'processing'
      const result = await deleteInvoice({ id: invoiceIdToDelete, companyId: company.id })

      const handled = handleResult(result, (errors) => {
        app.setFlash({
          type: 'wrong',
          message: errors.messages || i18n.t('errors.generic_error'),
        })
      })

      if (!handled.success) return handled

      setPosition((prev) => (prev ?? 0) - 1)
      setFilesStatus((prev) => prev.filter((el) => el !== fileToRemove))
      onFileRemove?.(fileToRemove)
    }

    if (fileToRemove.status === 'error') {
      setPosition((prev) => (prev ?? 0) - 1)
      setFilesStatus((prev) => prev.filter((el) => el !== fileToRemove))
    }
  }

  const processFiles = async (
    companyId: number,
    filesStatusParam: FileWithStatus<InvoiceFragment>[],
    pos: number
  ) => {
    const fileStatus = filesStatusParam[pos]
    const nextWaitingFileExists =
      fileStatus != null && pos < filesStatusParam.length && fileStatus.status === 'waiting'
    if (nextWaitingFileExists) {
      fileStatus.status = 'processing'
      try {
        const response = await uploadInvoice({
          companyId,
          file: fileStatus.file,
          resources: resources,
          vendorId,
        })
        const result = handleResult(response, (errors) => {
          app.setFlash({
            type: 'wrong',
            message: errors.messages || i18n.t('errors.generic_error'),
          })
        })
        if (result.success) {
          fileStatus.result =
            result.data.financialDocument == null ? undefined : result.data.financialDocument
          if (fileStatus.result) {
            const id = fileStatus.result.id
            setTimeout(() => getInvoice({ id }), 3000)
            return
          }
        } else {
          fileStatus.status = 'error'
        }
      } catch (error) {
        fileStatus.status = 'error'
      }
      // setPosition after error
      setPosition(pos + 1)
    } else if (position == filesStatusParam.length) {
      onUploadFinish(filesStatus)
    }
  }

  useUnsafeEffect(() => {
    if (invoice) {
      if (invoice.status === 'review') {
        // setPosition after success
        // eslint-disable-next-line no-type-assertion/no-type-assertion
        filesStatus[position ?? 0]!.status = 'success'
        // eslint-disable-next-line no-type-assertion/no-type-assertion
        filesStatus[position ?? 0]!.result = invoice
        setPosition((prev) => (prev ?? 0) + 1)
      } else {
        const id = invoice.id
        setTimeout(() => getInvoice({ id }), 3000)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoice])

  useEffect(() => {
    if (position !== null && position !== undefined && position >= 0) {
      processFiles(company.id, filesStatus, position)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [position])

  const disabled = !multiple && filesStatus.length > 0

  return (
    <FileWithStatusDropzone
      accept={{
        'image/jpeg': ['.jpg'],
        'image/png': ['.png'],
        'application/pdf': ['.pdf'],
      }}
      filesWithStatus={filesStatus}
      onDrop={onDropFile}
      onItemRemove={(el) => {
        onRemoveFile(el)
      }}
      multiple={multiple}
    >
      <Box flexDirection="column">
        <Text.Normal weight="bold" color={disabled ? 'grey600' : undefined}>
          {i18n.t('finance.invoices.upload_invoice_modal.choose_or_drag')}
        </Text.Normal>
        <Text.Normal size="200" color={disabled ? 'grey600' : undefined}>
          {i18n.t('finance.invoices.upload_invoice_modal.drag_and_drop')}
        </Text.Normal>
      </Box>
    </FileWithStatusDropzone>
  )
}
